import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import type { CorporateInquiryAdminStatus, GivingInterest } from '@/lib/corporate-inquiry';
import { addBrevoContact, parseNotificationEmails } from '@/lib/utils/brevo';
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, Search } from 'lucide-react';

const DEFAULT_CORPORATE_RECIPIENT = 'admin@gsgo.org';

const GIVING_INTEREST_LABELS: Record<GivingInterest, string> = {
  gold: 'Gold',
  silver: 'Silver',
  bronze: 'Bronze',
  unsure: 'Unsure',
};

interface CorporateInquiryRecord {
  id: string;
  companyName: string;
  contactName: string;
  email: string;
  phone?: string | null;
  companySize: string;
  givingInterest: GivingInterest;
  message?: string | null;
  selectedTier?: string | null;
  selectedAmount?: number | null;
  source: string;
  createdAt?: { toDate?: () => Date };
  notifiedAt?: { toDate?: () => Date };
  applicantNotifiedAt?: { toDate?: () => Date };
  brevoSyncedAt?: { toDate?: () => Date };
  adminStatus?: CorporateInquiryAdminStatus;
  adminNotes?: string | null;
  updatedAt?: { toDate?: () => Date };
}

interface AdminCorporateInquiriesProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const statusLabel: Record<CorporateInquiryAdminStatus, string> = {
  new: 'New',
  in_review: 'In Review',
  contacted: 'Contacted',
  closed: 'Closed',
};

const statusColorClass: Record<CorporateInquiryAdminStatus, string> = {
  new: 'bg-blue-100 text-blue-700',
  in_review: 'bg-amber-100 text-amber-700',
  contacted: 'bg-purple-100 text-purple-700',
  closed: 'bg-emerald-100 text-emerald-700',
};

function resolveAdminStatus(record: CorporateInquiryRecord): CorporateInquiryAdminStatus {
  return record.adminStatus ?? 'new';
}

function formatTimestamp(ts: { toDate?: () => Date } | undefined): string {
  if (!ts || typeof ts !== 'object' || typeof ts.toDate !== 'function') return '—';
  return ts.toDate().toLocaleString();
}

export function AdminCorporateInquiries({
  showNotify,
}: AdminCorporateInquiriesProps): JSX.Element {
  const [inquiries, setInquiries] = useState<CorporateInquiryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<CorporateInquiryAdminStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [adminNotesDraft, setAdminNotesDraft] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [brevoApiKeyFromDb, setBrevoApiKeyFromDb] = useState('');
  const [brevoCorporateListId, setBrevoCorporateListId] = useState<number | null>(null);
  const [corporateNotificationEmail, setCorporateNotificationEmail] = useState('');
  const [brevoSyncInProgress, setBrevoSyncInProgress] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const brevoRef = doc(db, 'artifacts', appId, 'settings', 'brevo');
    getDoc(brevoRef)
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const key = (snap.data()?.apiKey as string | undefined)?.trim() ?? '';
        if (key) setBrevoApiKeyFromDb(key);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const contactRef = doc(db, 'artifacts', appId, 'settings', 'contact');
    const unsubscribe = onSnapshot(
      contactRef,
      (snap) => {
        if (!snap.exists()) {
          setCorporateNotificationEmail('');
          setBrevoCorporateListId(null);
          return;
        }
        const data = snap.data();
        setCorporateNotificationEmail(
          (data.corporateNotificationEmail as string | undefined) ?? ''
        );
        const raw = data.brevoCorporatePartnersListId;
        if (raw == null || raw === '') {
          setBrevoCorporateListId(null);
          return;
        }
        const id = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
        if (Number.isInteger(id) && id > 0) setBrevoCorporateListId(id);
        else setBrevoCorporateListId(null);
      },
      (error) => {
        console.error(
          '[AdminCorporateInquiries] Failed to subscribe to contact settings:',
          error
        );
      }
    );
    return unsubscribe;
  }, []);

  const effectiveRecipients = useMemo(() => {
    const parsed = parseNotificationEmails(corporateNotificationEmail.trim());
    if (parsed.length > 0) return parsed.join(', ');
    return `${DEFAULT_CORPORATE_RECIPIENT} (default fallback)`;
  }, [corporateNotificationEmail]);

  const brevoListSummary = useMemo(() => {
    if (brevoCorporateListId != null && brevoCorporateListId > 0) {
      return `#${brevoCorporateListId} — Corporate Partners Applications`;
    }
    return 'Not configured — applicants will not sync to Brevo';
  }, [brevoCorporateListId]);

  const canSyncToBrevo =
    !!brevoApiKeyFromDb && brevoCorporateListId != null && brevoCorporateListId > 0;

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'corporateInquiries'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const records = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as CorporateInquiryRecord[];
        setInquiries(records);
        setLoading(false);
      },
      (error) => {
        console.error(
          '[AdminCorporateInquiries] Failed to subscribe to corporateInquiries. Admin list cannot refresh:',
          error
        );
        showNotify?.('Failed to load corporate inquiries.', 'error');
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [showNotify]);

  const filtered = useMemo(() => {
    const norm = search.trim().toLowerCase();
    return inquiries.filter((r) => {
      const status = resolveAdminStatus(r);
      const passStatus = statusFilter === 'all' ? true : status === statusFilter;
      if (!passStatus) return false;
      if (!norm) return true;
      const blob = [
        r.companyName,
        r.contactName,
        r.email,
        r.phone ?? '',
        r.message ?? '',
        GIVING_INTEREST_LABELS[r.givingInterest] ?? r.givingInterest,
        r.companySize,
        r.source,
        r.selectedTier ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return blob.includes(norm);
    });
  }, [inquiries, search, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const selected = useMemo(
    () => filtered.find((r) => r.id === selectedId) ?? paginated[0] ?? null,
    [filtered, selectedId, paginated]
  );

  useEffect(() => {
    if (!selected) {
      setAdminNotesDraft('');
      return;
    }
    setAdminNotesDraft(selected.adminNotes ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync notes when selection changes by id only
  }, [selected?.id]);

  const handleStatusUpdate = async (nextStatus: CorporateInquiryAdminStatus) => {
    if (!selected) return;
    setIsUpdating(true);
    try {
      await updateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'corporateInquiries', selected.id),
        {
          adminStatus: nextStatus,
          adminNotes: adminNotesDraft.trim() || null,
          updatedAt: serverTimestamp(),
        }
      );
      showNotify?.(
        `Inquiry for ${selected.companyName} moved to ${statusLabel[nextStatus]}.`,
        'success'
      );
    } catch (error) {
      console.error(
        '[AdminCorporateInquiries] Failed to update inquiry status:',
        selected.id,
        nextStatus,
        error
      );
      showNotify?.('Unable to update status. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSyncToBrevo = async (): Promise<void> => {
    if (!selected) return;
    if (!canSyncToBrevo) {
      showNotify?.(
        'Configure Brevo API key (Settings → Brevo API) and Corporate Partners list ID (Settings → Contact).',
        'error'
      );
      return;
    }
    setBrevoSyncInProgress(true);
    try {
      const firstName =
        selected.contactName?.split(/\s+/)[0]?.trim() || selected.contactName || undefined;
      const attributes: Record<string, string> = {};
      if (selected.companyName) attributes['COMPANY'] = selected.companyName;
      await addBrevoContact({
        apiKey: brevoApiKeyFromDb,
        email: selected.email,
        firstName,
        listIds: [brevoCorporateListId!],
        attributes: Object.keys(attributes).length > 0 ? attributes : undefined,
      });
      await updateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'corporateInquiries', selected.id),
        { brevoSyncedAt: serverTimestamp() }
      );
      showNotify?.(`${selected.email} added to Brevo corporate partners list.`, 'success');
    } catch (err) {
      console.error(
        `[AdminCorporateInquiries] Brevo CRM sync failed for ${selected.email}:`,
        err
      );
      showNotify?.(
        err instanceof Error ? err.message : 'Failed to sync to Brevo. Check Settings → Brevo API.',
        'error'
      );
    } finally {
      setBrevoSyncInProgress(false);
    }
  };

  if (loading) {
    return <div className="text-gray-600">Loading corporate inquiries...</div>;
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Corporate Inquiries</h2>
        <p className="text-sm text-gray-600 mt-1">
          Submissions from the corporate partnership form on /partners.
        </p>
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50/40 px-4 py-3 text-sm text-gray-700 space-y-1">
        <p>
          <span className="font-semibold text-gray-900">Admin notifications (CF-7):</span>{' '}
          {effectiveRecipients}
        </p>
        <p>
          <span className="font-semibold text-gray-900">Brevo CRM list:</span> {brevoListSummary}
        </p>
        <p className="text-xs text-gray-500">
          Change recipients and list ID in App Settings → Contact.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by company, contact, email..."
              className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CorporateInquiryAdminStatus | 'all')
            }
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="in_review">In Review</option>
            <option value="contacted">Contacted</option>
            <option value="closed">Closed</option>
          </select>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            <span className="font-semibold">{filtered.length}</span> inquiry(ies)
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.2fr),minmax(0,1fr)] gap-4">
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Tier interest
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-sm text-center text-gray-500">
                      No corporate inquiries found.
                    </td>
                  </tr>
                ) : (
                  paginated.map((record) => {
                    const status = resolveAdminStatus(record);
                    const active =
                      (selected?.id ?? selectedId ?? paginated[0]?.id) === record.id;
                    const syncBadges = [
                      record.notifiedAt ? 'Admin ✓' : null,
                      record.applicantNotifiedAt ? 'Applicant ✓' : null,
                      record.brevoSyncedAt ? 'Brevo ✓' : null,
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <tr
                        key={record.id}
                        onClick={() => setSelectedId(record.id)}
                        className={`cursor-pointer transition-colors ${
                          active ? 'bg-blue-50/70' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-3 align-top">
                          <p className="text-sm font-semibold text-gray-900">{record.companyName}</p>
                          <p className="text-xs text-gray-600">{record.contactName}</p>
                          <p className="text-xs text-gray-500">{record.email}</p>
                          {syncBadges ? (
                            <p className="text-[10px] text-gray-400 mt-0.5">{syncBadges}</p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">
                          {GIVING_INTEREST_LABELS[record.givingInterest] ?? record.givingInterest}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusColorClass[status]}`}
                          >
                            {statusLabel[status]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 align-top">
                          {formatTimestamp(record.createdAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
          {selected ? (
            <>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selected.companyName}</h3>
                <p className="text-sm text-gray-600">{selected.contactName}</p>
                <p className="text-sm text-gray-600">{selected.email}</p>
                {selected.phone && <p className="text-sm text-gray-600">{selected.phone}</p>}
              </div>

              <div className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <p className="font-semibold text-gray-900">Company size</p>
                  <p className="text-gray-700">{selected.companySize}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Giving interest</p>
                  <p className="text-gray-700">
                    {GIVING_INTEREST_LABELS[selected.givingInterest] ?? selected.givingInterest}
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Source</p>
                  <p className="text-gray-700">{selected.source}</p>
                </div>
                {selected.message && (
                  <div>
                    <p className="font-semibold text-gray-900">Message</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{selected.message}</p>
                  </div>
                )}
                {(selected.selectedTier || selected.selectedAmount != null) && (
                  <div>
                    <p className="font-semibold text-gray-900">Calculator selection</p>
                    <p className="text-gray-700">
                      {selected.selectedTier ? `Tier: ${selected.selectedTier}` : ''}
                      {selected.selectedAmount != null
                        ? `${selected.selectedTier ? ' · ' : ''}$${selected.selectedAmount.toLocaleString('en-US')}`
                        : ''}
                    </p>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900">Admin notification (CF-7)</p>
                  <p className="text-gray-700">
                    {selected.notifiedAt
                      ? `Sent ${formatTimestamp(selected.notifiedAt)}`
                      : 'Not yet sent to staff (check Brevo key / function logs)'}
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Applicant confirmation email</p>
                  <p className="text-gray-700">
                    {selected.applicantNotifiedAt
                      ? `Sent ${formatTimestamp(selected.applicantNotifiedAt)}`
                      : 'Not yet sent — redeploy CF-7 or check Brevo sender / spam folder'}
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Brevo CRM list</p>
                  <p className="text-gray-700">
                    {selected.brevoSyncedAt
                      ? `Synced ${formatTimestamp(selected.brevoSyncedAt)}`
                      : canSyncToBrevo
                        ? 'Not synced yet — use Sync to Brevo below or wait for CF-7.'
                        : 'List ID not configured — set Corporate Partners list ID in Settings → Contact.'}
                  </p>
                  {canSyncToBrevo && (
                    <button
                      type="button"
                      disabled={brevoSyncInProgress}
                      onClick={() => void handleSyncToBrevo()}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      {brevoSyncInProgress ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                      Sync to Brevo
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900">Admin notes</label>
                <textarea
                  rows={4}
                  value={adminNotesDraft}
                  onChange={(e) => setAdminNotesDraft(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Partnership review notes, follow-up outcomes..."
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => handleStatusUpdate('in_review')}
                  className="inline-flex items-center justify-center gap-1 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
                >
                  Mark In Review
                </button>
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => handleStatusUpdate('contacted')}
                  className="rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-60"
                >
                  Mark Contacted
                </button>
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => handleStatusUpdate('new')}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  Reset to New
                </button>
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={() => handleStatusUpdate('closed')}
                  className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Select an inquiry to view details and update status.
            </p>
          )}
        </div>
      </div>

      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Showing{' '}
              <span className="font-medium text-gray-900">
                {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, filtered.length)}
              </span>{' '}
              of <span className="font-medium text-gray-900">{filtered.length}</span> inquiry
              {filtered.length !== 1 ? 'ies' : ''}
            </span>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Rows per page
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page <span className="font-medium text-gray-900">{currentPage}</span> of{' '}
              <span className="font-medium text-gray-900">{totalPages}</span>
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
